// src/services/streamingService.ts
import type { User } from 'firebase/auth';
import type { Message } from '@/lib/firebase';
import type { ChatMessage as LocalChatMessage, Document } from '@/types';
import type { SelectedItem, FirstImpressionTool } from '@/types';
import type { AccessControl } from '@/lib/firebase';
import { handleMessageStreaming } from '@/utils/chatStreaming';
import { v4 as uuidv4 } from 'uuid';
import { createUserInteractionTrace } from '@/utils/langfuseEnhanced';

export interface StreamingServiceConfig {
  assistantId: string;
  templateId?: string;
  accessControl?: AccessControl;
  name: string;
  avatar: string;
  initialInstructions: string;
  initialDocuments: Document[];
  tools: string[];
  toolConfigs: Record<string, Record<string, any>>;
  adminEmail?: string;
  senderName?: string;
}

export interface StreamingCallbacks {
  onStreamStart: () => void;
  onStreamChunk: (chunk: string) => void;
  onThinkingContent: (content: string) => void;
  onArtifactContent?: (artifacts: import('@/types').ArtifactData[]) => void;
  onStreamEnd: (finalContent: string, thinkingContent: string) => void;
  onStreamError: (error: unknown) => void;
}

export class StreamingService {
  /**
   * Validate assistant configuration
   */
  static validateConfig(config: StreamingServiceConfig): void {
    if (!config.assistantId || config.assistantId.trim() === '') {
      throw new Error('Assistant ID is required');
    }

    if (!config.name || config.name.trim() === '') {
      throw new Error('Assistant name is required');
    }

    if (!config.initialInstructions || config.initialInstructions.trim() === '') {
      throw new Error('Initial instructions are required');
    }

    // Validate tool configurations
    if (config.toolConfigs) {
      for (const [toolName, toolConfig] of Object.entries(config.toolConfigs)) {
        if (toolConfig === null || toolConfig === undefined) {
          throw new Error(`Invalid tool configuration for ${toolName}`);
        }
      }
    }
  }

  /**
   * Build message payload for API calls
   */
  static buildMessagePayload(
    message: string,
    config: StreamingServiceConfig,
    sessionId: string,
    traceId: string,
    confirmedTools?: FirstImpressionTool[]
  ): Record<string, any> {
    const payload: Record<string, any> = {
      message,
      assistantId: config.assistantId,
      sessionId,
      traceId,
      assistantConfig: config
    };

    if (confirmedTools && confirmedTools.length > 0) {
      payload.toolConfirmation = confirmedTools;
    }

    return payload;
  }

  /**
   * Categorize errors for proper handling
   */
  static categorizeError(error: Error): {
    type: 'network' | 'validation' | 'authentication' | 'system';
    retryable: boolean;
    userMessage: string;
  } {
    const message = error.message.toLowerCase();

    if (message.includes('failed to fetch') || message.includes('network')) {
      return {
        type: 'network',
        retryable: true,
        userMessage: 'Network connection failed. Please try again.'
      };
    }

    if (message.includes('required') || message.includes('invalid')) {
      return {
        type: 'validation',
        retryable: false,
        userMessage: `Invalid request: ${error.message}`
      };
    }

    if (message.includes('authenticated') || message.includes('unauthorized')) {
      return {
        type: 'authentication',
        retryable: false,
        userMessage: 'Authentication required. Please login and try again.'
      };
    }

    return {
      type: 'system',
      retryable: true,
      userMessage: 'An unexpected error occurred. Please try again.'
    };
  }

  /**
   * Send a regular user message with streaming response
   */
  static async sendMessage(
    message: string,
    config: StreamingServiceConfig,
    context: {
      currentUser: User | null;
      firestoreMessages: Message[];
      sessionId: string;
      selectedItems: SelectedItem[];
      artifactContent?: string; // Optional artifact content to include
      signal: AbortSignal;
    },
    callbacks: StreamingCallbacks
  ): Promise<string> {
    if (!context.currentUser?.email || !config.assistantId) {
      throw new Error('User not authenticated or assistant not configured');
    }

    const traceId = `msg-${Date.now()}-${uuidv4()}`;
    let accumulatedThinking = '';
    
    // Create Langfuse trace with assistant name
    await createUserInteractionTrace({
      traceId,
      sessionId: context.sessionId,
      userId: context.currentUser.uid,
      userEmail: context.currentUser.email,
      assistantId: config.assistantId,
      assistantName: config.name,  // Use the assistant name
      message: message,
      selectedItemsCount: context.selectedItems?.length
    });
    
    // Append artifact content to message if provided
    const messageWithArtifacts = context.artifactContent 
      ? `${message}${context.artifactContent}`
      : message;

    await handleMessageStreaming(
      {
        message: messageWithArtifacts,
        assistantId: config.assistantId,
        templateId: config.templateId,
        accessControl: config.accessControl,
        name: config.name,
        avatar: config.avatar,
        initialInstructions: config.initialInstructions,
        initialDocuments: config.initialDocuments,
        tools: config.tools,
        toolConfigs: config.toolConfigs,
        currentUser: context.currentUser,
        firestoreMessages: context.firestoreMessages,
        sessionId: context.sessionId,
        traceId,
        selectedItems: context.selectedItems,
        adminEmail: config.adminEmail,
        senderName: config.senderName,
        signal: context.signal
      },
      {
        onStreamStart: callbacks.onStreamStart,
        onStreamChunk: (chunk) => {
          callbacks.onStreamChunk(chunk);
        },
        onThinkingContent: (content) => {
          accumulatedThinking += content;
          callbacks.onThinkingContent(accumulatedThinking);
        },
        onArtifactContent: callbacks.onArtifactContent,
        onStreamEnd: (finalContent) => {
          callbacks.onStreamEnd(finalContent, accumulatedThinking);
        },
        onStreamError: callbacks.onStreamError
      }
    );

    return traceId;
  }

  /**
   * Send a tool confirmation message with streaming response
   */
  static async sendToolConfirmationMessage(
    userMessage: string,
    confirmedTools: FirstImpressionTool[],
    config: StreamingServiceConfig,
    context: {
      currentUser: User | null;
      firestoreMessages: Message[];
      sessionId: string;
      selectedItems: SelectedItem[];
      traceId: string;
      artifactContent?: string; // Optional artifact content to include
      signal: AbortSignal;
    },
    callbacks: StreamingCallbacks
  ): Promise<void> {
    if (!context.currentUser?.email || !config.assistantId) {
      throw new Error('User not authenticated or assistant not configured');
    }

    let accumulatedThinking = '';
    
    // Append artifact content to message if provided
    const messageWithArtifacts = context.artifactContent 
      ? `${userMessage}${context.artifactContent}`
      : userMessage;

    await handleMessageStreaming(
      {
        message: messageWithArtifacts,
        assistantId: config.assistantId,
        templateId: config.templateId,
        accessControl: config.accessControl,
        name: config.name,
        avatar: config.avatar,
        initialInstructions: config.initialInstructions,
        initialDocuments: config.initialDocuments,
        tools: config.tools,
        toolConfigs: config.toolConfigs,
        currentUser: context.currentUser,
        firestoreMessages: context.firestoreMessages,
        sessionId: context.sessionId,
        traceId: context.traceId,
        selectedItems: context.selectedItems,
        adminEmail: config.adminEmail,
        senderName: config.senderName,
        signal: context.signal,
        // Tool confirmation parameters
        confirm_past_pause: true,
        tools_to_use: confirmedTools.length > 0 ? confirmedTools : undefined
      },
      {
        onStreamStart: callbacks.onStreamStart,
        onStreamChunk: (chunk) => {
          callbacks.onStreamChunk(chunk);
        },
        onThinkingContent: (content) => {
          accumulatedThinking += content;
          callbacks.onThinkingContent(accumulatedThinking);
        },
        onArtifactContent: callbacks.onArtifactContent,
        onStreamEnd: (finalContent) => {
          callbacks.onStreamEnd(finalContent, accumulatedThinking);
        },
        onStreamError: callbacks.onStreamError
      }
    );
  }
}